import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Collapse,
    IconButton,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import { IconUpload, IconChevronDown, IconChevronUp, IconCheck, IconX, IconArrowBackUp } from '@tabler/icons';
import api from '../../../../services/api';
import PageHeader from '../../../../../ui-component/extended/PageHeader';

const formatCurrencyBR = (value) => {
    const number = Number(value);
    if (Number.isNaN(number)) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(number);
};

const formatDateBR = (value) => {
    if (!value) return '-';
    const [year, month, day] = value.split('-');
    return `${day}/${month}/${year}`;
};

const STATUS_LABEL = {
    unmatched: { label: 'Não conciliado', color: 'warning' },
    matched: { label: 'Conciliado', color: 'success' },
    ignored: { label: 'Ignorado', color: 'default' }
};

const BankReconciliation = () => {
    const [appId, setAppId] = useState(null);
    const [bankAccounts, setBankAccounts] = useState([]);
    const [bankAccountFilter, setBankAccountFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('unmatched');
    const [lines, setLines] = useState([]);
    const [expanded, setExpanded] = useState({});
    const [candidates, setCandidates] = useState({});
    const [uploadBankAccount, setUploadBankAccount] = useState('');
    const [status, setStatus] = useState({ type: '', message: '' });

    useEffect(() => {
        (async () => {
            const res = await api.get('v1/apps/default/');
            setAppId(res.data.app.id);
            const baRes = await api.get(`v1/apps/${res.data.app.id}/bank-accounts/?company=${res.data.default_company_id}`);
            setBankAccounts(baRes.data.bank_accounts || []);
        })();
    }, []);

    useEffect(() => {
        if (!appId) return;
        listarLinhas();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId, bankAccountFilter, statusFilter]);

    const listarLinhas = async () => {
        const params = new URLSearchParams();
        if (bankAccountFilter) params.set('bank_account', bankAccountFilter);
        if (statusFilter) params.set('status', statusFilter);
        const res = await api.get(`v1/apps/${appId}/bank-statement-lines/?${params.toString()}`);
        setLines(res.data.lines || []);
    };

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        if (!uploadBankAccount) {
            setStatus({ type: 'error', message: 'Selecione a conta bancária antes de importar o arquivo' });
            return;
        }
        const formData = new FormData();
        formData.append('bank_account', uploadBankAccount);
        formData.append('file', file);
        try {
            // Não define Content-Type manualmente - o axios/navegador precisa
            // calcular o boundary do multipart sozinho a partir do FormData;
            // um header fixo aqui quebraria o parse no backend.
            const res = await api.post(`v1/apps/${appId}/bank-statement-lines/`, formData);
            setStatus({ type: 'success', message: `${res.data.lines.length} linha(s) importada(s).` });
            listarLinhas();
        } catch (error) {
            setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao importar extrato' });
        }
    };

    const toggleExpand = async (lineId) => {
        setExpanded((prev) => ({ ...prev, [lineId]: !prev[lineId] }));
        if (!candidates[lineId]) {
            const res = await api.get(`v1/apps/${appId}/bank-statement-lines/${lineId}/candidates/`);
            setCandidates((prev) => ({ ...prev, [lineId]: res.data.candidates || [] }));
        }
    };

    const handleMatch = async (lineId, candidate) => {
        const payload = candidate.type === 'receipt' ? { receipt: candidate.id } : { payment: candidate.id };
        await api.post(`v1/apps/${appId}/bank-statement-lines/${lineId}/match/`, payload);
        listarLinhas();
    };

    const handleUnmatch = async (lineId) => {
        await api.post(`v1/apps/${appId}/bank-statement-lines/${lineId}/unmatch/`, {});
        listarLinhas();
    };

    const handleIgnore = async (lineId) => {
        await api.post(`v1/apps/${appId}/bank-statement-lines/${lineId}/ignore/`, {});
        listarLinhas();
    };

    return (
        <Box>
            <PageHeader
                icon={IconUpload}
                gradient="blue"
                title="Conciliação Bancária"
                subtitle="Importe o extrato (CSV) e confira com os pagamentos/recebimentos já lançados"
                actions={
                    <Stack direction="row" spacing={2}>
                        <TextField
                            select
                            size="small"
                            label="Conta pra importar"
                            sx={{ width: 220 }}
                            value={uploadBankAccount}
                            onChange={(e) => setUploadBankAccount(e.target.value)}
                        >
                            {bankAccounts.map((ba) => (
                                <MenuItem key={ba.id} value={ba.id}>
                                    {ba.bank_name} - CC {ba.account_number}
                                </MenuItem>
                            ))}
                        </TextField>
                        <Button variant="contained" component="label" startIcon={<IconUpload size={18} />}>
                            Importar CSV
                            <input type="file" accept=".csv" hidden onChange={handleUpload} />
                        </Button>
                    </Stack>
                }
            />

            {status.message && (
                <Typography color={status.type === 'error' ? 'error.main' : 'success.main'} sx={{ mb: 2 }}>
                    {status.message}
                </Typography>
            )}

            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <TextField
                    select
                    size="small"
                    label="Conta bancária"
                    sx={{ width: 220 }}
                    value={bankAccountFilter}
                    onChange={(e) => setBankAccountFilter(e.target.value)}
                >
                    <MenuItem value="">Todas</MenuItem>
                    {bankAccounts.map((ba) => (
                        <MenuItem key={ba.id} value={ba.id}>
                            {ba.bank_name} - CC {ba.account_number}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    select
                    size="small"
                    label="Status"
                    sx={{ width: 180 }}
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="unmatched">Não conciliado</MenuItem>
                    <MenuItem value="matched">Conciliado</MenuItem>
                    <MenuItem value="ignored">Ignorado</MenuItem>
                </TextField>
            </Stack>

            <Card>
                <CardContent>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox" />
                                <TableCell>Data</TableCell>
                                <TableCell>Descrição</TableCell>
                                <TableCell align="right">Valor</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {lines.map((line) => {
                                const statusInfo = STATUS_LABEL[line.status] || { label: line.status, color: 'default' };
                                const isExpanded = !!expanded[line.id];
                                return (
                                    <React.Fragment key={line.id}>
                                        <TableRow hover>
                                            <TableCell padding="checkbox">
                                                {line.status === 'unmatched' && (
                                                    <IconButton size="small" onClick={() => toggleExpand(line.id)}>
                                                        {isExpanded ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
                                                    </IconButton>
                                                )}
                                            </TableCell>
                                            <TableCell>{formatDateBR(line.date)}</TableCell>
                                            <TableCell>{line.description}</TableCell>
                                            <TableCell align="right" sx={{ color: Number(line.amount) >= 0 ? 'success.main' : 'error.main' }}>
                                                {formatCurrencyBR(line.amount)}
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={statusInfo.label} color={statusInfo.color} size="small" />
                                            </TableCell>
                                            <TableCell align="right">
                                                {line.status === 'unmatched' && (
                                                    <IconButton size="small" onClick={() => handleIgnore(line.id)} title="Ignorar">
                                                        <IconX size={16} />
                                                    </IconButton>
                                                )}
                                                {line.status === 'matched' && (
                                                    <IconButton size="small" onClick={() => handleUnmatch(line.id)} title="Desfazer conciliação">
                                                        <IconArrowBackUp size={16} />
                                                    </IconButton>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell colSpan={6} sx={{ py: 0, border: 0 }}>
                                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                    <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1, my: 1 }}>
                                                        <Typography variant="subtitle2" fontWeight={600} mb={1}>
                                                            Sugestões de conciliação
                                                        </Typography>
                                                        {(candidates[line.id] || []).length === 0 ? (
                                                            <Typography color="text.secondary" variant="body2">
                                                                Nenhum pagamento/recebimento compatível encontrado (mesmo valor, data próxima, ainda não conciliado).
                                                            </Typography>
                                                        ) : (
                                                            <Table size="small">
                                                                <TableBody>
                                                                    {(candidates[line.id] || []).map((candidate) => (
                                                                        <TableRow key={`${candidate.type}-${candidate.id}`}>
                                                                            <TableCell>{candidate.organization_name}</TableCell>
                                                                            <TableCell>{candidate.description}</TableCell>
                                                                            <TableCell>{formatDateBR(candidate.date)}</TableCell>
                                                                            <TableCell align="right">{formatCurrencyBR(candidate.amount)}</TableCell>
                                                                            <TableCell align="right">
                                                                                <Button
                                                                                    size="small"
                                                                                    startIcon={<IconCheck size={14} />}
                                                                                    onClick={() => handleMatch(line.id, candidate)}
                                                                                >
                                                                                    Conciliar
                                                                                </Button>
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    ))}
                                                                </TableBody>
                                                            </Table>
                                                        )}
                                                    </Box>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                );
                            })}
                            {lines.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        Nenhuma linha de extrato encontrada
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </Box>
    );
};

export default BankReconciliation;
